import React, { useRef } from 'react';
import { BASE_URL } from '../../shared/constants';
import { formatExpectedArrivalDate } from '../../utils/utils';
import { Link } from 'react-router-dom';
import { isOrderItemInStock } from '../../utils/stockQuantities';
import { orderItemKey } from '../../utils/orderItemKeys';
import ProductDiscountImage from '../ProductDiscountImage';
import { productImagePaths } from '../../utils/productImages';
import '../../styles/admin-order-items-mobile.css';

const formatPrice = value => String(value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
const isActionTarget = target => Boolean(target.closest('button, a, input, select, textarea, label'));
const MAX_TAP_DURATION_MS = 350;
const DOUBLE_TAP_INTERVAL_MS = 400;
const MAX_TAP_MOVEMENT_PX = 12;
const MAX_DOUBLE_TAP_DISTANCE_PX = 24;
const TOUCH_CLICK_SUPPRESSION_MS = 600;

const OrderItemMobile = ({ item, setModalImage, handleEditClick, isLast, selected, onToggleSelect }) => {
  const imagePath = productImagePaths(item)[0];
  const isPreorder = item.state?.databaseValue === 'PREORDER';
  const expectedArrival = isPreorder ? formatExpectedArrivalDate(item.expectedArrivalDate) : null;
  const touchStart = useRef(null);
  const lastTap = useRef(null);
  const lastTouchEnd = useRef(-Infinity);
  const cancelTouch = () => {
    touchStart.current = null;
    lastTap.current = null;
  };
  const handlePointerDown = event => {
    if (event.pointerType !== 'touch') return;
    if (event.isPrimary === false || isActionTarget(event.target)) {
      cancelTouch();
      return;
    }
    touchStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY, time: event.timeStamp };
  };
  const handlePointerMove = event => {
    const start = touchStart.current;
    if (start?.id === event.pointerId
        && Math.hypot(event.clientX - start.x, event.clientY - start.y) > MAX_TAP_MOVEMENT_PX) {
      cancelTouch();
    }
  };
  const handlePointerUp = event => {
    if (event.pointerType !== 'touch') return;
    lastTouchEnd.current = event.timeStamp;
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || start.id !== event.pointerId || isActionTarget(event.target)
        || event.timeStamp - start.time > MAX_TAP_DURATION_MS
        || Math.hypot(event.clientX - start.x, event.clientY - start.y) > MAX_TAP_MOVEMENT_PX) {
      lastTap.current = null;
      return;
    }
    const previous = lastTap.current;
    if (previous && event.timeStamp - previous.time <= DOUBLE_TAP_INTERVAL_MS
        && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= MAX_DOUBLE_TAP_DISTANCE_PX) {
      lastTap.current = null;
      event.preventDefault();
      onToggleSelect(orderItemKey(item));
    } else {
      lastTap.current = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    }
  };
  const handleDoubleClick = event => {
    // Touch-generated dblclick must not repeat selection or bypass rejected touch gestures.
    if (isActionTarget(event.target) || event.timeStamp - lastTouchEnd.current < TOUCH_CLICK_SUPPRESSION_MS) return;
    event.preventDefault();
    onToggleSelect(orderItemKey(item));
  };

  return (
    <div className={`admin-order-item${selected ? ' admin-order-item--selected' : ''}${isLast ? ' admin-order-item--last' : ''}${isOrderItemInStock(item) ? '' : ' order-item-status-warning'}`}
      role='group' aria-label={item.name} onDoubleClick={onToggleSelect ? handleDoubleClick : undefined}
      onPointerDown={onToggleSelect ? handlePointerDown : undefined}
      onPointerMove={onToggleSelect ? handlePointerMove : undefined}
      onPointerUp={onToggleSelect ? handlePointerUp : undefined} onPointerCancel={cancelTouch}>
      {onToggleSelect && <input type='checkbox' className='sr-only admin-order-item__select' checked={Boolean(selected)}
        onChange={() => onToggleSelect(orderItemKey(item))} aria-label={`Обрати ${item.name}`} />}
      <div className='admin-order-item__heading'>
        {imagePath && (
          <button type='button' className='admin-order-item__photo'
            onClick={() => setModalImage(imagePath)} data-toggle='modal' data-target='#imageModal'
            aria-label={`Збільшити фото ${item.name}`}>
            <ProductDiscountImage price={item.price} discountedPrice={item.discountedPrice}
              personalDiscount={item.personalDiscount} productDiscount={item.productDiscount}>
              <img src={BASE_URL + '/api/v1/product/image/' + imagePath} alt={item.name} />
            </ProductDiscountImage>
          </button>
        )}
        <div className='admin-order-item__heading-text'>
          <div className='admin-order-item__name'>
            {item.name}
            {item.state?.databaseValue && !isPreorder && item.parentProductName && (
              <span className='font-weight-bold'> [{item.parentProductName}]</span>
            )}
            {' '}<Link to={'/product/' + item.productId} target='_blank' rel='noopener noreferrer'
              className='admin-order-item__product-link' aria-label={`Відкрити товар ${item.name}`}>
              <i className='bi bi-box-arrow-up-right' aria-hidden='true' />
            </Link>
          </div>
          {isPreorder && <div className='admin-order-item__preorder'>
            Передзамовлення{expectedArrival ? ` · очікується ${expectedArrival}` : ''}
          </div>}
        </div>
      </div>

      <div className='admin-order-item__details'>
        {(item.productCode || item.barcode) && <div className='admin-order-item__identifiers'>
          {item.productCode && <div className='admin-order-item__reference'><span>Артикул:</span><b>{item.productCode}</b></div>}
          {item.barcode && <div className='admin-order-item__reference admin-order-item__barcode'>
            <i className='bi bi-upc-scan' aria-hidden='true' />
            <span className='sr-only'>Штрихкод:</span><b>{item.barcode}</b>
          </div>}
        </div>}
        {item.unsentQuantity != null && <div><span>На складі:</span><b>{item.unsentQuantity} шт</b></div>}
      </div>

      <div className='admin-order-item__footer'>
        <div className='admin-order-item__price-line'>
          <button type='button' className='admin-order-item__price'
            onClick={() => handleEditClick(item)} data-toggle='modal' data-target='#changeDiscountModal'
            aria-label={`Змінити знижку для ${item.name}`}>
            {item.price !== item.discountedPrice && <s>{formatPrice(item.price)}</s>}
            <strong>{formatPrice(item.discountedPrice)} грн</strong>
          </button>
          <span className='admin-order-item__times'>×</span>
          <button type='button' className={`admin-order-item__quantity${item.quantity > 1 ? ' admin-order-item__quantity--multiple' : ''}`}
            onClick={() => handleEditClick(item)} data-toggle='modal' data-target='#editQuantityModal'
            aria-label={`Змінити кількість ${item.name}`}>
            {item.quantity} шт
          </button>
        </div>
        <div className='admin-order-item__actions'>
          <button type='button' onClick={() => handleEditClick(item)}
            data-toggle='modal' data-target='#editProductOrderModal' aria-label={`Редагувати ${item.name}`}>
            <i className='bi bi-pencil' aria-hidden='true' />
          </button>
          <button type='button' className='admin-order-item__delete' onClick={() => handleEditClick(item)}
            data-toggle='modal' data-target='#confirmDeleteModal' aria-label={`Видалити ${item.name}`}>
            <i className='bi bi-trash' aria-hidden='true' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderItemMobile;
